import React from 'react';
import Switch from 'antd/es/switch';
import { Button, SliderInput, Title } from '@meitu/saas-ui';
import { ArtTextCharacterConfig } from '@meitu/saas-editor-core/es/layers/text-layer';
import { IAdvancedTextMaterial, ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import LabelAction from '@meitu/saas-editor-ui/es/components/label-action';
import { useTextRange } from '@meitu/saas-editor-ui/es/hooks/use-text-range-attrs-state';
import { TextMarkSvgUpload } from '@/editor/components/text-mark-svg-upload';
import { editor } from '@/instance';
import { useTextMarkModel } from '@/store/models/text-mark-model';
import './text-art-panel.less';

export const TextArtPanelKey = 'TEXT_ART';

const { SimpleTitle } = Title;

type AdjustArtTextSingleCharacterParams = Pick<ArtTextCharacterConfig, 'rotate' | 'scale' | 'offsetX' | 'offsetY'>;

export const TextArtConfig: React.FC<{ layerId: string }> = React.memo(({ layerId }) => {
    const editingRange = useTextRange(layerId);
    const layer = editor.services.flatLayer.getLayer(layerId);
    const textContents = (layer?.data as ITextLayerProps | undefined)?.textContents || [];
    const textContent = (() => {
        if (!editingRange || editingRange.start === editingRange.end) return null;
        let byteOffset = 0;
        return textContents.find((content) => {
            const nextByteOffset = byteOffset + new TextEncoder().encode(content.text).length;
            const selected = editingRange.start < nextByteOffset && editingRange.end > byteOffset;
            byteOffset = nextByteOffset;
            return selected;
        });
    })();

    const handleChange = (key: keyof AdjustArtTextSingleCharacterParams, val: number, isAfterChange = false) => {
        const partialConfig: AdjustArtTextSingleCharacterParams = {
            rotate: textContent?.rotate ?? 0,
            scale: textContent?.scale ?? 1,
            offsetX: textContent?.offsetX ?? 0,
            offsetY: textContent?.offsetY ?? 0,
        };
        partialConfig[key] = val;
        editor.textLayer.adjustArtTextSingleCharacter(layerId, partialConfig, {
            writeHistory: isAfterChange,
            range: editingRange,
        });
    };

    if (!editingRange || editingRange.start === editingRange.end) return null;

    return (
        <>
            <LabelAction title={'旋转'} align={true}>
                <div style={{ width: 154 }}>
                    <SliderInput
                        value={textContent?.rotate ?? 0}
                        max={359}
                        min={0}
                        showStepBtn={true}
                        type="horizontal"
                        onChange={(val: number) => {
                            handleChange('rotate', val);
                        }}
                        onAfterChange={(val: number) => {
                            handleChange('rotate', val, true);
                        }}
                    />
                </div>
            </LabelAction>

            <LabelAction title={'缩放'} align={true}>
                <div style={{ width: 154 }}>
                    <SliderInput
                        value={textContent?.scale ?? 1}
                        max={2}
                        min={0.1}
                        showStepBtn={true}
                        type="horizontal"
                        onChange={(val: number) => {
                            handleChange('scale', val);
                        }}
                        onAfterChange={(val: number) => {
                            handleChange('scale', val, true);
                        }}
                    />
                </div>
            </LabelAction>

            <LabelAction title={'左右偏移'} align={true}>
                <div style={{ width: 154 }}>
                    <SliderInput
                        value={textContent?.offsetX ?? 0}
                        max={50}
                        min={-50}
                        showStepBtn={true}
                        type="horizontal"
                        onChange={(val: number) => {
                            handleChange('offsetX', val);
                        }}
                        onAfterChange={(val: number) => {
                            handleChange('offsetX', val, true);
                        }}
                    />
                </div>
            </LabelAction>
            <LabelAction title={'上下偏移'} align={true}>
                <div style={{ width: 154 }}>
                    <SliderInput
                        value={textContent?.offsetY ?? 0}
                        max={50}
                        min={-50}
                        showStepBtn={true}
                        type="horizontal"
                        onChange={(val: number) => {
                            handleChange('offsetY', val);
                        }}
                        onAfterChange={(val: number) => {
                            handleChange('offsetY', val, true);
                        }}
                    />
                </div>
            </LabelAction>
        </>
    );
});

const TextArtPanel = () => {
    const textLayerId = useTextMarkModel((state) => state.textLayerId);
    const textArtConfig = useTextMarkModel((state) => state.textArtConfig);
    const setTextArtConfig = useTextMarkModel((state) => state.setTextArtConfig);

    const layer = editor.layer.getLayer(textLayerId);
    const advancedTextMaterial = (layer?.data as ITextLayerProps)?.advancedTextMaterial;

    if (!layer || !advancedTextMaterial) return null;

    const handleChangeUseBlackText = (val: boolean) => {
        setTextArtConfig({
            useBlackText: +val,
        });
    };

    return (
        <div className="p-text-mark-panel">
            <div className="p-text-mark-panel__title">
                <SimpleTitle text="文字特效" fontSize={16} />
                <Button
                    type="ghost"
                    size="small"
                    onClick={() =>
                        TextMarkSvgUpload.show(
                            'textArt',
                            textLayerId,
                            async (data: IAdvancedTextMaterial) => {
                                editor.textLayer.applyArtText(textLayerId, data);
                                setTextArtConfig({
                                    useBlackText: 1,
                                });
                            },
                            true,
                        )
                    }
                >
                    替换素材
                </Button>
            </div>

            <div className="text-mark-panel__content">
                <LabelAction title={'缩略图强制黑色字'} align={true}>
                    <Switch
                        checked={!!textArtConfig?.useBlackText}
                        onChange={(val: boolean) => {
                            handleChangeUseBlackText(val);
                        }}
                    />
                </LabelAction>
            </div>
        </div>
    );
};

export default React.memo(TextArtPanel);
